/**
 * Slack DMs (Threadbot) settings panel.
 *
 * Lets an org admin paste a Slack Bot User OAuth Token (xoxb-…) and the
 * Slack App ID for their org's bot (Threadbot for ThreadHeads — any Slack
 * app for other orgs). Powers @-mention DM notifications on PO + Product
 * Dev comments.
 *
 * The token is sent to the backend as plaintext over TLS and stored in
 * Supabase Vault (encrypted at rest). It is never returned to the UI;
 * only a boolean `hasBotToken` is exposed. To rotate, paste a new one.
 * To remove DMs, click Clear.
 *
 * Multi-tenant: each org configures its own Slack app. Empty state =
 * "@-mention DMs are off for this org" — the comment notifier silently
 * skips.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { noAutofillProps } from '@/components/ui/no-autofill';
import { SecretInput } from '@/components/ui/secret-input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Save, Trash2, Loader2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';

/** Slack logo mark, for the "Add to Slack" button. */
function SlackMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.124 2.521a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.52 2.521h-2.522V8.834zm-1.268 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.167 0a2.528 2.528 0 0 1 2.523 2.522v6.312zm-2.523 10.124a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.167 24a2.527 2.527 0 0 1-2.52-2.52v-2.523h2.52zm0-1.268a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.52 2.523h-6.313z" />
    </svg>
  );
}

export function SlackThreadbotPanel() {
  const utils = trpc.useUtils();
  const statusQuery = trpc.slackThreadbot.getStatus.useQuery(undefined, {
    staleTime: 30_000,
  });

  const setConfig = trpc.slackThreadbot.setConfig.useMutation({
    onSuccess: () => {
      toast.success('Slack DMs configured');
      void utils.slackThreadbot.getStatus.invalidate();
      setBotToken('');
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'Failed to save'),
  });

  const clearConfig = trpc.slackThreadbot.clearConfig.useMutation({
    onSuccess: () => {
      toast.success('Slack DM config cleared');
      void utils.slackThreadbot.getStatus.invalidate();
      setAppId('');
      setBotToken('');
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'Failed to clear'),
  });

  const [appId, setAppId] = useState('');
  const [botToken, setBotToken] = useState('');

  // Sync initial App ID from the server once it loads. Bot token never
  // round-trips back, so the input always starts empty.
  useEffect(() => {
    if (statusQuery.data?.appId && !appId) {
      setAppId(statusQuery.data.appId);
    }
  }, [statusQuery.data?.appId, appId]);

  const hasToken = statusQuery.data?.hasBotToken ?? false;
  const hasAppId = Boolean(statusQuery.data?.appId);
  const isConfigured = hasToken && hasAppId;

  const handleSave = () => {
    const trimmedAppId = appId.trim();
    const trimmedToken = botToken.trim();
    if (!trimmedAppId && !trimmedToken) {
      toast.error('Enter an App ID or paste a Bot Token');
      return;
    }
    if (trimmedToken && !trimmedToken.startsWith('xoxb-')) {
      toast.error('Slack Bot OAuth tokens must start with "xoxb-"');
      return;
    }
    setConfig.mutate({
      appId: trimmedAppId || undefined,
      botToken: trimmedToken || undefined,
    });
  };

  const handleClear = () => {
    if (
      !confirm(
        'Remove Slack DM config for this org? @-mention DMs will stop until you configure again.',
      )
    )
      return;
    clearConfig.mutate();
  };

  const oauthConfigured = statusQuery.data?.oauthConfigured ?? false;
  const [installing, setInstalling] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);

  const handleAddToSlack = async () => {
    setInstalling(true);
    try {
      const { url } = await utils.slackThreadbot.getInstallUrl.fetch();
      window.location.href = url; // off to Slack's consent screen
    } catch (err) {
      setInstalling(false);
      toast.error(err instanceof Error ? err.message : 'Could not start Slack connect');
    }
  };

  // Handle the OAuth return (?slack_connected / ?slack_error) once on mount,
  // then strip the params so a refresh doesn't re-toast.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('slack_connected') === 'true') {
      const team = params.get('team');
      toast.success(team ? `Slack connected to ${team}` : 'Slack connected');
      void utils.slackThreadbot.getStatus.invalidate();
    } else if (params.get('slack_error')) {
      toast.error(`Slack connect failed: ${params.get('slack_error')}`);
    } else {
      return;
    }
    params.delete('slack_connected');
    params.delete('slack_error');
    params.delete('team');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-threadheads-purple" />
            Slack DMs (Threadbot)
          </CardTitle>
          {isConfigured ? (
            <Badge variant="success">Active</Badge>
          ) : (
            <Badge variant="secondary">Not configured</Badge>
          )}
        </div>
        <CardDescription>
          Connects your Slack workspace so PrintRun can post digests and DM
          teammates who get @-mentioned on Purchase Orders or Product Dev. The
          token is stored in Supabase Vault, never logged, and never returned to
          this page.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {oauthConfigured && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {isConfigured
                ? 'Connected to your Slack workspace. Reconnect to refresh permissions, or disconnect to turn it off.'
                : "Connect in one click. You'll authorise in Slack, then land back here. No token handling."}
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                onClick={() => void handleAddToSlack()}
                disabled={installing}
                className="bg-[#4A154B] text-white hover:bg-[#4A154B]/90"
              >
                {installing ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <SlackMark className="h-4 w-4 mr-2" />
                )}
                {isConfigured ? 'Reconnect Slack' : 'Add to Slack'}
              </Button>
              {isConfigured && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleClear}
                  disabled={clearConfig.isPending}
                  className="text-destructive hover:text-destructive"
                >
                  {clearConfig.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Disconnect
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Manual / advanced: paste a bot token. The only path when OAuth isn't
            configured server-side; collapsed-by-default otherwise. */}
        <div className={oauthConfigured ? 'border-t pt-3' : ''}>
          {oauthConfigured && (
            <button
              type="button"
              onClick={() => setManualOpen((v) => !v)}
              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            >
              <ChevronDown className={cn('h-3 w-3 transition-transform', !manualOpen && '-rotate-90')} />
              Connect manually with a bot token instead
            </button>
          )}
          {(manualOpen || !oauthConfigured) && (
            <div className="space-y-4 mt-3">
        <div className="space-y-1.5">
          <Label htmlFor="slack-app-id">Slack App ID</Label>
          <Input
            id="slack-app-id"
            value={appId}
            onChange={(e) => setAppId(e.target.value)}
            placeholder="A09B4QJBDMW"
            className="font-mono text-sm"
            disabled={setConfig.isPending || statusQuery.isLoading}
            {...noAutofillProps}
          />
          <p className="text-xs text-muted-foreground">
            Found at https://api.slack.com/apps → your app → "App ID" near the top.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="slack-bot-token">
            Bot User OAuth Token{' '}
            {hasToken && (
              <span className="text-xs text-muted-foreground font-normal">
                (paste a new one to rotate)
              </span>
            )}
          </Label>
          <SecretInput
            id="slack-bot-token"
            value={botToken}
            onChange={(e) => setBotToken(e.target.value)}
            placeholder={hasToken ? '••••••••••••' : 'xoxb-…'}
            className="font-mono text-sm"
            disabled={setConfig.isPending}
          />
          <p className="text-xs text-muted-foreground">
            Slack app → "OAuth & Permissions" → Bot User OAuth Token. Required
            scopes: <code className="font-mono">chat:write</code>,{' '}
            <code className="font-mono">im:write</code>,{' '}
            <code className="font-mono">users:read</code>,{' '}
            <code className="font-mono">users:read.email</code>.
          </p>
        </div>

        <div className="flex items-center justify-between pt-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClear}
            disabled={!isConfigured || clearConfig.isPending}
            className="text-destructive hover:text-destructive"
          >
            {clearConfig.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
            ) : (
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
            )}
            Clear config
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={
              setConfig.isPending || (!appId.trim() && !botToken.trim())
            }
          >
            {setConfig.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
            ) : (
              <Save className="h-3.5 w-3.5 mr-1.5" />
            )}
            Save
          </Button>
        </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
